import React from "react";
import Link from "next/link";
import "../../css/KeyFeatures.css";
import {
  AlertCircle,
  Search,
  Users,
  BarChart,
  Stethoscope,
  Shield,
  Syringe,
  TrendingUp,
  Building,
  BookOpen,
  Route,
  ClipboardCheck,
  MessageSquare,
  FileSearch,
  PieChart,
  ChevronRight,
  ShieldCheck,
  FolderOpen,
  Activity,
  HeartPulse,
  Settings,
  Puzzle
} from "lucide-react";

const qapiFeatures = [
  { title: "Action Plans", icon: ClipboardCheck, link: "/features/action-plans" },
  { title: "Audit Management", icon: ShieldCheck, link: "/features/audit-management" },
  { title: "Critical Elements Pathways", icon: Route, link: "/features/critical-element-pathways" },
  { title: "Facility Assessment", icon: Building, link: "/features/facility-assessment" },
  { title: "Grievance Management", icon: AlertCircle, link: "/features/grievance-management" },
  { title: "Incident Investigation", icon: Search, link: "/features/incident-investigation" },
  { title: "Medical Director Engagement & Compliance", icon: Stethoscope, link: "/features/medical-directorship-engagement-compliance" },
  { title: "Mock Surveys", icon: FileSearch, link: "/features/mock-surveys" },
  { title: "Performance Improvement Plans (PIPs)", icon: TrendingUp, link: "/features/performance-improvement-plans" },
  { title: "Committees and Meetings", icon: Users, link: "/features/committee-meeting-management" },
  { title: "Survey Management & Insights", icon: PieChart, link: "/features/survey-management" },
];

const healthFeatures = [
  { title: "OSHA Respirator Protection Program", icon: Shield, link: "/osha-respiratory-protection" },
  { title: "Vaccination Tracking & Reportable Conditions", icon: Syringe, link: "/features/vaccination-tracking-management" },
];

const operationsFeatures = [
  { title: "Document Vault", icon: FolderOpen, link: "/features/document-vault" },
  { title: "Feedback & Satisfaction Surveys", icon: MessageSquare, link: "/features/feedback-and-satisfaction-surveys" },
  { title: "Metrics & Task Action Plans", icon: BarChart, link: "/features/metric-task-action-plans" },
  { title: "Policy & Procedure Management", icon: BookOpen, link: "/features/policy-and-procedure-management" },
];

const KeyFeatures = () => {
  return (
    <section className="key-features">
      <div className="kf-header">
        <h2>KEY FEATURES</h2>
        <p>Everything you need to manage quality, employee health, and operational excellence.</p>
      </div>

      <div className="key-features-container">
        <div className="kf-architecture-grid key-features-grid">
        {/* Card 1: QAPI */}
        <div className="kf-arch-card kf-arch-card-left qapi-card side-card">
          <div className="kf-arch-card-header">
            <div className="kf-arch-illustration kf-illust-qapi">
              <img 
                src="/assets/key-features/qapi-quality-management.svg" 
                alt="QAPI Quality Management" 
                className="feature-card-image"
                style={{ width: '100%', objectFit: 'contain', transform: 'scale(1.35)' }} 
              />
            </div>
            <div className="kf-arch-body feature-card-content">
              <h3 className="feature-card-title">QAPI & Quality Management</h3>
              <p className="feature-card-description">Manage assessments, audits, grievances, surveys, action plans, and quality improvement from a single platform.</p>
            </div>
          </div>
          <div className="kf-arch-divider">
            <span>FEATURES INSIDE</span>
          </div>
          <div className="kf-arch-list">
            {qapiFeatures.map((f, i) => (
              <Link href={f.link} key={i} className="kf-list-item feature-link">
                <f.icon className="kf-list-icon text-green" size={20} />
                <span className="kf-list-title">{f.title}</span>
                <ChevronRight className="kf-list-chevron" size={16} />
              </Link>
            ))}
          </div>
        </div>

        <div className="right-feature-column">
          {/* Card 2: Health */}
          <div className="kf-arch-card kf-arch-card-right side-card">
          <div className="kf-arch-card-header">
            <div className="kf-arch-illustration kf-illust-health">
              <img 
                src="/assets/key-features/employee-health.png" 
                alt="Employee Health" 
                className="feature-card-image"
                style={{ width: '100%', objectFit: 'contain', transform: 'scale(1.2)' }} 
              />
            </div>
            <div className="kf-arch-body feature-card-content">
              <h3 className="feature-card-title">Employee Health</h3>
              <p className="feature-card-description">Track vaccinations, employee health requirements, and OSHA compliance across every facility.</p>
            </div>
          </div>
          <div className="kf-arch-divider text-green">
            <span>FEATURES INSIDE</span>
          </div>
          <div className="kf-arch-list">
            {healthFeatures.map((f, i) => (
              <Link href={f.link} key={i} className="kf-list-item feature-link">
                <f.icon className="kf-list-icon text-blue" size={20} />
                <span className="kf-list-title">{f.title}</span>
                <ChevronRight className="kf-list-chevron" size={16} />
              </Link>
            ))}
          </div>
        </div>

        {/* Card 3: Operations */}
          <div className="kf-arch-card kf-arch-card-right side-card">
          <div className="kf-arch-card-header">
            <div className="kf-arch-illustration kf-illust-ops">
              <img 
                src="/assets/key-features/operational-efficiency.svg" 
                alt="Operational Efficiency" 
                className="feature-card-image"
                style={{ width: '100%', objectFit: 'contain', transform: 'scale(1.15)' }} 
              />
            </div>
            <div className="kf-arch-body feature-card-content">
              <h3 className="feature-card-title">Operational Efficiency & Insights</h3>
              <p className="feature-card-description">Automate recurring workflows, policies, metrics, and reporting with real-time visibility.</p>
            </div>
          </div>
          <div className="kf-arch-divider text-green">
            <span>FEATURES INSIDE</span>
          </div>
          <div className="kf-arch-list">
            {operationsFeatures.map((f, i) => (
              <Link href={f.link} key={i} className="kf-list-item feature-link">
                <f.icon className="kf-list-icon text-green" size={20} />
                <span className="kf-list-title">{f.title}</span>
                <ChevronRight className="kf-list-chevron" size={16} />
              </Link>
            ))}
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Footer Strip */}
      <div className="footer-strip">
        <div className="footer-strip-text">
          <strong><span className="logo-care">CARE</span><span className="logo-ascendium">ASCENDIUM</span></strong> brings it all together so you can focus on what matters most—your residents and your team.
        </div>
        <img 
          src="/footer-teamwork-illust.png" 
          alt="Team Work" 
          className="footer-strip-image"
        />
      </div>
    </section>
  );
};

export default KeyFeatures;
